import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../shared/context/ThemeContext';
import {
  getPrEvents,
  getComponents,
  getBusinessTransactionsByTimeFilter,
  getBusinessTransactionsByDateRange,
  getAlerts,
  getIncidents,
  getGithubDashboard,
  getTenants,
} from '../../shared/api/api';
import LoadingSpinner from '../../shared/components/LoadingSpinner';
import SearchableSelect from './SearchableSelect';
import TransactionStatusBanner from './TransactionStatusBanner';
import { TX_STATUS_IDLE, nextTxStatus, isBlankFailure, isDegraded } from './txFetchStatus';
import { getDisplayName } from '../../shared/auth/session';
import './DashboardPageV2.css';

// ── Response Time chart ────────────────────────────────────────────────────────
function ResponseTimeChart({ data, labels }) {
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(600);
  const [tooltip, setTooltip] = useState(null);
  const [animPct, setAnimPct] = useState(0);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    setWidth(el.getBoundingClientRect().width || 600);
    const ro = new ResizeObserver(entries => setWidth(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    setAnimPct(0);
    const duration = 1000;
    const start = performance.now();
    const animate = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setAnimPct(eased);
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [data]);

  const H = 220, padL = 36, padR = 10, padT = 10, padB = 24;
  const W = width;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;
  const hasData = data.some(v => v > 0);
  const max = Math.max(...data, 1);
  const n = data.length;

  const toX = i => padL + (i / (n - 1)) * chartW;
  const toY = v => padT + (1 - v / max) * chartH;

  const animData = data.map(v => v * animPct);
  const xs = animData.map((_, i) => toX(i));
  const ys = animData.map(v => toY(v));

  const pathD = xs.reduce((d, x, i) => {
    if (i === 0) return `M${x},${ys[i]}`;
    const cpx = (xs[i - 1] + x) / 2;
    return d + ` C${cpx},${ys[i-1]} ${cpx},${ys[i]} ${x},${ys[i]}`;
  }, '');
  const areaD = `${pathD} L${xs[n-1]},${padT + chartH} L${xs[0]},${padT + chartH} Z`;
  const gridFracs = [0, 0.25, 0.5, 0.75, 1];

  const handleMouseMove = (e) => {
    const rect = wrapRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    let closest = 0, minDist = Infinity;
    xs.forEach((x, i) => {
      const d = Math.abs(x - mouseX);
      if (d < minDist) { minDist = d; closest = i; }
    });
    setTooltip({ i: closest, x: xs[closest], y: ys[closest] });
  };

  const ttPad = 8, ttW = 96, ttH = 42;

  return (
    <div ref={wrapRef} style={{ width: '100%', position: 'relative' }}>
      {!hasData && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>No duration data available</span>
        </div>
      )}
      <svg
        width={W} height={H}
        style={{ display: 'block', overflow: 'visible' }}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setTooltip(null)}
      >
        <defs>
          <linearGradient id="rtGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ff7c43" stopOpacity="0.45"/>
            <stop offset="100%" stopColor="#ff7c43" stopOpacity="0"/>
          </linearGradient>
        </defs>
        {gridFracs.map(f => {
          const y = padT + (1 - f) * chartH;
          const label = f === 0 ? '0' : `${Math.round(f * max)}ms`;
          return (
            <g key={f}>
              <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="rgba(128,128,128,0.12)" strokeWidth="1"/>
              <text x={padL - 4} y={y + 4} textAnchor="end" fontSize="9" fill="#6b7a99">{label}</text>
            </g>
          );
        })}
        {[0, 6, 12, 18, 23].map(i => (
          <text key={i} x={toX(i)} y={H - 6} textAnchor="middle" fontSize="10" fill="#6b7a99">{labels[i]}</text>
        ))}
        {hasData && <path d={areaD} fill="url(#rtGrad)"/>}
        {hasData && <path d={pathD} fill="none" stroke="#ff7c43" strokeWidth="2" strokeLinejoin="round"/>}

        {tooltip && hasData && data[tooltip.i] > 0 && (
          <g>
            <line x1={tooltip.x} y1={padT} x2={tooltip.x} y2={padT + chartH} stroke="rgba(128,128,128,0.3)" strokeWidth="1" strokeDasharray="3 3"/>
            <circle cx={tooltip.x} cy={tooltip.y} r="4" fill="#ff7c43" stroke="#fff" strokeWidth="1.5"/>
            {(() => {
              const flipX = tooltip.x + ttPad + ttW > W - padR;
              const tx = flipX ? tooltip.x - ttPad - ttW : tooltip.x + ttPad;
              const ty = Math.max(padT, tooltip.y - ttH / 2);
              return (
                <g>
                  <rect x={tx} y={ty} width={ttW} height={ttH} rx="6"
                    fill="var(--surface2)" stroke="var(--border)" strokeWidth="1"/>
                  <text x={tx + ttW / 2} y={ty + 14} textAnchor="middle" fontSize="10" fill="#6b7a99" fontFamily="inherit">
                    {labels[tooltip.i]}
                  </text>
                  <text x={tx + ttW / 2} y={ty + 30} textAnchor="middle" fontSize="13" fontWeight="700" fill="#ff7c43" fontFamily="inherit">
                    {data[tooltip.i]}ms avg
                  </text>
                </g>
              );
            })()}
          </g>
        )}
      </svg>
    </div>
  );
}

// ── Volume chart (measures its own width) ─────────────────────────────────────
function VolumeChart({ data, labels }) {
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(600);
  const [tooltip, setTooltip] = useState(null);
  const [animPct, setAnimPct] = useState(0);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    setWidth(el.getBoundingClientRect().width || 600);
    const ro = new ResizeObserver(entries => {
      setWidth(entries[0].contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    setAnimPct(0);
    const duration = 1000;
    const start = performance.now();
    const animate = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setAnimPct(eased);
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [data]);

  const H = 150, padL = 24, padR = 10, padT = 10, padB = 24;
  const W = width;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;
  const max = Math.max(...data, 1);
  const n = data.length;

  const toX = i => padL + (i / (n - 1)) * chartW;
  const toY = v => padT + (1 - v / max) * chartH;

  const animData = data.map(v => v * animPct);
  const xs = animData.map((_, i) => toX(i));
  const ys = animData.map(v => toY(v));

  const pathD = xs.reduce((d, x, i) => {
    if (i === 0) return `M${x},${ys[i]}`;
    const cpx = (xs[i - 1] + x) / 2;
    return d + ` C${cpx},${ys[i-1]} ${cpx},${ys[i]} ${x},${ys[i]}`;
  }, '');
  const areaD = `${pathD} L${xs[n-1]},${padT + chartH} L${xs[0]},${padT + chartH} Z`;
  const gridFracs = [0, 0.25, 0.5, 0.75, 1];

  const handleMouseMove = (e) => {
    const rect = wrapRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    // find closest hour bucket
    let closest = 0, minDist = Infinity;
    xs.forEach((x, i) => {
      const d = Math.abs(x - mouseX);
      if (d < minDist) { minDist = d; closest = i; }
    });
    setTooltip({ i: closest, x: xs[closest], y: ys[closest] });
  };

  const ttPad = 8, ttW = 90, ttH = 42;

  return (
    <div ref={wrapRef} style={{ width: '100%', position: 'relative' }}>
      <svg
        width={W} height={H}
        style={{ display: 'block', overflow: 'visible' }}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setTooltip(null)}
      >
        <defs>
          <linearGradient id="vGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#bf14cc" stopOpacity="0.45"/>
            <stop offset="100%" stopColor="#bf14cc" stopOpacity="0"/>
          </linearGradient>
        </defs>
        {gridFracs.map(f => {
          const y = padT + (1 - f) * chartH;
          return (
            <g key={f}>
              <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="rgba(128,128,128,0.12)" strokeWidth="1"/>
              <text x={padL - 4} y={y + 4} textAnchor="end" fontSize="10" fill="#6b7a99">{Math.round(f * max)}</text>
            </g>
          );
        })}
        {[0, 6, 12, 18, 23].map(i => (
          <text key={i} x={toX(i)} y={H - 6} textAnchor="middle" fontSize="10" fill="#6b7a99">{labels[i]}</text>
        ))}
        <path d={areaD} fill="url(#vGrad)"/>
        <path d={pathD} fill="none" stroke="#bf14cc" strokeWidth="2" strokeLinejoin="round"/>

        {tooltip && (
          <g>
            {/* vertical crosshair */}
            <line x1={tooltip.x} y1={padT} x2={tooltip.x} y2={padT + chartH} stroke="rgba(128,128,128,0.3)" strokeWidth="1" strokeDasharray="3 3"/>
            {/* dot on line */}
            <circle cx={tooltip.x} cy={tooltip.y} r="4" fill="#bf14cc" stroke="#fff" strokeWidth="1.5"/>
            {/* tooltip box — flip to left side if too close to right edge */}
            {(() => {
              const flipX = tooltip.x + ttPad + ttW > W - padR;
              const tx = flipX ? tooltip.x - ttPad - ttW : tooltip.x + ttPad;
              const ty = Math.max(padT, tooltip.y - ttH / 2);
              return (
                <g>
                  <rect x={tx} y={ty} width={ttW} height={ttH} rx="6"
                    fill="var(--surface2)" stroke="var(--border)" strokeWidth="1"/>
                  <text x={tx + ttW / 2} y={ty + 14} textAnchor="middle" fontSize="10" fill="#6b7a99" fontFamily="inherit">
                    {labels[tooltip.i]}
                  </text>
                  <text x={tx + ttW / 2} y={ty + 30} textAnchor="middle" fontSize="13" fontWeight="700" fill="#bf14cc" fontFamily="inherit">
                    {data[tooltip.i]} tx
                  </text>
                </g>
              );
            })()}
          </g>
        )}
      </svg>
    </div>
  );
}

// ── SVG tick ring ──────────────────────────────────────────────────────────────
function TickRing({ pct, color, dimColor, size = 160, animate = false }) {
  const [displayPct, setDisplayPct] = useState(animate ? 0 : pct);

  useEffect(() => {
    if (!animate) { setDisplayPct(pct); return; }
    setDisplayPct(0);
    const duration = 1200;
    const start = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayPct(eased * pct);
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [pct, animate]);

  const cx = size / 2, cy = size / 2, r = size * 0.4125;
  const ticks = 60;
  const activeCount = Math.round((displayPct / 100) * ticks);
  const tickLen = size * 0.05, tickW = size * 0.02;
  const innerR = r - tickLen;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {Array.from({ length: ticks }, (_, i) => {
        const angle = (i / ticks) * 360 - 90;
        const rad = (angle * Math.PI) / 180;
        const x1 = cx + innerR * Math.cos(rad);
        const y1 = cy + innerR * Math.sin(rad);
        const x2 = cx + r * Math.cos(rad);
        const y2 = cy + r * Math.sin(rad);
        return (
          <line
            key={i}
            x1={x1} y1={y1} x2={x2} y2={y2}
            stroke={i < activeCount ? color : dimColor}
            strokeWidth={tickW}
            strokeLinecap="round"
          />
        );
      })}
    </svg>
  );
}

// ── Gauge widget ───────────────────────────────────────────────────────────────
function GaugeWidget({ pct, light }) {
  const [displayPct, setDisplayPct] = useState(0);

  useEffect(() => {
    const duration = 1200;
    const start = performance.now();
    const animate = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayPct(+((eased * pct).toFixed(1)));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [pct]);
  const size = 200;
  const cx = size / 2, cy = size / 2;
  const r = 78;
  const strokeW = 14;
  const toRad = deg => (deg * Math.PI) / 180;

  const START = 135;
  const TOTAL = 270;

  // single full-arc path for the gradient track
  const fullArc = () => {
    // split into two arcs (can't do > 180° in one SVG arc)
    const mid = START + TOTAL / 2;
    const pt = d => ({ x: cx + r * Math.cos(toRad(d)), y: cy + r * Math.sin(toRad(d)) });
    const s = pt(START), m = pt(mid), e = pt(START + TOTAL);
    return `M ${s.x} ${s.y} A ${r} ${r} 0 0 1 ${m.x} ${m.y} A ${r} ${r} 0 0 1 ${e.x} ${e.y}`;
  };

  const needleAngle = START + (displayPct / 100) * TOTAL;
  const needleLen = r - 4;
  const tailLen = 12;
  const nx = cx + needleLen * Math.cos(toRad(needleAngle));
  const ny = cy + needleLen * Math.sin(toRad(needleAngle));
  const tx = cx - tailLen * Math.cos(toRad(needleAngle));
  const ty = cy - tailLen * Math.sin(toRad(needleAngle));

  const color = pct >= 95 ? '#00d97e' : pct >= 75 ? '#a3d977' : pct >= 50 ? '#ffb830' : '#ff4d6d';
  const label = pct >= 95 ? 'Healthy' : pct >= 75 ? 'Good' : pct >= 50 ? 'Degraded' : 'Unhealthy';

  // gradient stop positions along the arc (as percentages of the 270° sweep)
  // red→orange→yellow→lightgreen→green, smoothly blended
  const gradId = 'gaugeGrad';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      <svg width={size} height={size} style={{ overflow: 'visible' }}>
        <defs>
          {/* linear gradient rotated to roughly follow the arc sweep */}
          <linearGradient id={gradId} x1="0%" y1="100%" x2="100%" y2="100%">
            <stop offset="0%"   stopColor="#ff4d6d" />
            <stop offset="50%"  stopColor="#ffb830" />
            <stop offset="75%"  stopColor="#a3d977" />
            <stop offset="100%" stopColor="#00d97e" />
          </linearGradient>
          <filter id="gauge-shadow">
            <feDropShadow dx="0" dy="1" stdDeviation="1.5" floodColor="rgba(0,0,0,0.5)" />
          </filter>
        </defs>

        {/* dim background track */}
        <path
          d={fullArc()}
          fill="none"
          stroke={light ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.06)'}
          strokeWidth={strokeW}
          strokeLinecap="round"
        />

        {/* coloured gradient track on top */}
        <path
          d={fullArc()}
          fill="none"
          stroke={`url(#${gradId})`}
          strokeWidth={strokeW}
          strokeLinecap="round"
        />

        {/* needle soft glow */}
        <line x1={cx} y1={cy} x2={nx} y2={ny} stroke={light ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.15)'} strokeWidth={6} strokeLinecap="round" />
        {/* needle body */}
        <line x1={cx} y1={cy} x2={nx} y2={ny} stroke={light ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.4)'} strokeWidth={1.5} strokeLinecap="round" />
        <line
          x1={cx + (needleLen - 22) * Math.cos(toRad(needleAngle))}
          y1={cy + (needleLen - 22) * Math.sin(toRad(needleAngle))}
          x2={nx} y2={ny}
          stroke={light ? 'rgba(0,0,0,0.85)' : 'rgba(255,255,255,0.95)'} strokeWidth={1.5} strokeLinecap="round"
        />
        {/* pivot */}
        <circle cx={cx} cy={cy} r={10} fill="var(--surface2)" stroke={light ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.15)'} strokeWidth={1.5} />
        <circle cx={cx} cy={cy} r={3.5} fill={light ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.7)'} />
      </svg>

      <span style={{ fontSize: '1.7rem', fontWeight: 800, color, lineHeight: 1, marginTop: -20 }}>{displayPct}%</span>
      <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color, marginTop: 2 }}>{label}</span>
    </div>
  );
}

// ── SVG segmented bar ──────────────────────────────────────────────────────────
function SegBar({ pct, color, dimColor }) {
  const segs = 40, segW = 4, gap = 2;
  const totalW = segs * segW + (segs - 1) * gap;
  const activeCount = Math.round((pct / 100) * segs);

  return (
    <svg
      className="v2-h-seg-bar"
      viewBox={`0 0 ${totalW} 8`}
      preserveAspectRatio="none"
    >
      {Array.from({ length: segs }, (_, i) => (
        <rect
          key={i}
          x={i * (segW + gap)} y={0}
          width={segW} height={8} rx={2}
          fill={i < activeCount ? color : dimColor}
        />
      ))}
    </svg>
  );
}

// ── Requests-per-component horizontal bar chart ────────────────────────────────
function ReqBarChart({ data }) {
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) setWidth(w);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (!data || data.length === 0) return <div ref={wrapRef} style={{ width: '100%' }}><p className="v2-empty">No data.</p></div>;
  if (width === 0) return <div ref={wrapRef} style={{ width: '100%', height: 10 }} />;

  const padL = 108, padR = 16, padT = 10, padB = 30;
  const barH = 22, gap = 16;
  const vbW = width;
  const vbH = padT + data.length * barH + (data.length - 1) * gap + padB;
  const chartW = Math.max(vbW - padL - padR, 40);
  const chartH = vbH - padT - padB;
  const maxVal = Math.max(...data.map(d => d.count), 1);

  const tickCount = 5;
  const niceMax = Math.ceil(maxVal / tickCount) * tickCount || tickCount;
  const xTicks = Array.from({ length: tickCount + 1 }, (_, i) => Math.round((niceMax / tickCount) * i));

  const slotH = barH + gap;

  return (
    <div ref={wrapRef} style={{ width: '100%' }}>
      <svg width={vbW} height={vbH} style={{ display: 'block', width: '100%' }}>
        <defs>
          <linearGradient id="reqGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#bf14cc" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#7c3aed" stopOpacity="0.7" />
          </linearGradient>
        </defs>

        {/* x-axis grid lines + tick labels */}
        {xTicks.map(tick => {
          const x = padL + (tick / niceMax) * chartW;
          return (
            <g key={tick}>
              <line
                x1={x} y1={padT} x2={x} y2={padT + chartH}
                stroke="rgba(128,128,128,0.12)" strokeWidth={1}
              />
              <text
                x={x} y={padT + chartH + 18}
                textAnchor="middle" fontSize={10} fill="rgba(107,122,153,0.9)" fontFamily="inherit"
              >
                {tick}
              </text>
            </g>
          );
        })}

        {/* bars */}
        {data.map(({ comp, count }, i) => {
          const midY = padT + i * slotH + slotH / 2;
          const fillW = Math.max((count / niceMax) * chartW, 2);
          return (
            <g key={comp}>
              <text
                x={padL - 8} y={midY}
                textAnchor="end" dominantBaseline="middle"
                fontSize={11} fill="rgba(160,174,209,0.85)" fontFamily="inherit"
                clipPath={`url(#labelClip-${i})`}
              >
                {comp}
              </text>
              <clipPath id={`labelClip-${i}`}>
                <rect x={0} y={midY - slotH / 2} width={padL - 10} height={slotH} />
              </clipPath>
              <rect
                x={padL} y={midY - barH / 2}
                width={chartW} height={barH} rx={4}
                fill="rgba(128,128,128,0.08)"
              />
              <rect
                x={padL} y={midY - barH / 2}
                width={fillW} height={barH} rx={4}
                fill="url(#reqGrad)"
              />
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ── PR Trend line chart ────────────────────────────────────────────────────────
function PrTrendChart({ prEvents, timeRange }) {
  const wrapRef = useRef(null);
  const [dims, setDims] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => setDims({ w: el.getBoundingClientRect().width, h: el.getBoundingClientRect().height });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const numDays = timeRange === '24h' ? 1 : timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : null;
  const cutoff = numDays ? Date.now() - numDays * 86400000 : 0;
  const scopedPrs = prEvents.filter(p => new Date(p.createdAt).getTime() >= cutoff);
  const days = [...new Set(scopedPrs.map(p => { const d = new Date(p.createdAt); d.setHours(0,0,0,0); return d.getTime(); }))].sort((a,b)=>a-b).map(t => new Date(t));
  const multiYear = days.length > 1 && days[0].getFullYear() !== days[days.length - 1].getFullYear();
  const labels = days.map(d => {
    if (timeRange === 'all') {
      return multiYear
        ? d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: '2-digit' })
        : d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
    }
    return d.toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric' });
  });
  const buckets = days.map(d => {
    const start = new Date(d); start.setHours(0,0,0,0);
    const end = new Date(d); end.setHours(23,59,59,999);
    const dp = scopedPrs.filter(p => { const c = new Date(p.createdAt); return c >= start && c <= end; });
    return {
      passed: dp.filter(p => ['PASSED','APPROVED','REVIEW_COMPLETED'].includes((p.status||'').toUpperCase())).length,
      failed: dp.filter(p => ['FAILED','REJECTED'].includes((p.status||'').toUpperCase())).length,
      other:  dp.filter(p => ['PENDING','IN_PROGRESS','OPEN'].includes((p.status||'').toUpperCase())).length,
    };
  });

  const W = dims.w || 1, H = dims.h || 1;
  const padL = 24, padR = 30, padT = 12, padB = 24;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;
  const n = days.length;

  if (scopedPrs.length === 0) {
    return (
      <div ref={wrapRef} style={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p className="v2-empty">No PR events in this time range.</p>
      </div>
    );
  }

  const maxVal = Math.max(...buckets.map(b => b.passed + b.failed + b.other), 1);
  const toX = i => padL + (n === 1 ? chartW / 2 : (i / (n - 1)) * chartW);
  const toY = v => padT + (1 - v / maxVal) * chartH;
  const smooth = vals => vals.map((v, i) => {
    const x = toX(i), y = toY(v);
    if (i === 0) return `M${x},${y}`;
    const cpx = (toX(i-1) + x) / 2;
    return ` C${cpx},${toY(vals[i-1])} ${cpx},${y} ${x},${y}`;
  }).join('');

  const passVals = buckets.map(b => b.passed);
  const failVals = buckets.map(b => b.failed);
  const otherVals = buckets.map(b => b.other);
  const pp = smooth(passVals), fp = smooth(failVals), op = smooth(otherVals);
  const area = (path) => `${path} L${toX(n-1)},${padT+chartH} L${toX(0)},${padT+chartH} Z`;

  return (
    <div ref={wrapRef} style={{ flex: 1, minHeight: 0, width: '100%', overflow: 'hidden', minWidth: 0 }}>
      {dims.w > 0 && (
        <svg width={W} height={H} style={{ display: 'block', maxWidth: '100%' }}>
          <defs>
            <linearGradient id="ptPassGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#00d97e" stopOpacity="0.3"/><stop offset="100%" stopColor="#00d97e" stopOpacity="0"/></linearGradient>
            <linearGradient id="ptFailGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#ff4d6d" stopOpacity="0.2"/><stop offset="100%" stopColor="#ff4d6d" stopOpacity="0"/></linearGradient>
            <linearGradient id="ptOtherGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#ffb830" stopOpacity="0.2"/><stop offset="100%" stopColor="#ffb830" stopOpacity="0"/></linearGradient>
          </defs>
          {[0, 0.5, 1].map(f => <line key={f} x1={padL} y1={padT+(1-f)*chartH} x2={W-padR} y2={padT+(1-f)*chartH} stroke="rgba(128,128,128,0.12)" strokeWidth="1"/>)}
          <path d={area(op)} fill="url(#ptOtherGrad)"/>
          <path d={area(pp)} fill="url(#ptPassGrad)"/>
          <path d={area(fp)} fill="url(#ptFailGrad)"/>
          <path d={op} fill="none" stroke="#ffb830" strokeWidth="2"/>
          <path d={pp} fill="none" stroke="#00d97e" strokeWidth="2"/>
          <path d={fp} fill="none" stroke="#ff4d6d" strokeWidth="2"/>
          {days.map((_, i) => (
            <g key={i}>
              <circle cx={toX(i)} cy={toY(otherVals[i])} r="3" fill="#ffb830" stroke="#0e1420" strokeWidth="1.5"/>
              <circle cx={toX(i)} cy={toY(passVals[i])}  r="3" fill="#00d97e" stroke="#0e1420" strokeWidth="1.5"/>
              <circle cx={toX(i)} cy={toY(failVals[i])}  r="3" fill="#ff4d6d" stroke="#0e1420" strokeWidth="1.5"/>
              <text x={toX(i)} y={H-5} textAnchor="middle" fontSize="9" fill="#6b7a99">{labels[i]}</text>
            </g>
          ))}
        </svg>
      )}
    </div>
  );
}

// ── helpers ────────────────────────────────────────────────────────────────────
function timeAgo(dateStr) {
  if (!dateStr) return '—';
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ${Math.floor((diff % 3600) / 60)}m ago`;
  return new Date(dateStr).toLocaleDateString();
}

function severityClass(sev) {
  if (!sev) return 'med';
  const s = sev.toString().toUpperCase();
  if (s === 'CRITICAL') return 'crit';
  if (s === 'HIGH') return 'high';
  if (s === 'MEDIUM' || s === 'MED') return 'med';
  return 'ok';
}

function statusClass(status) {
  if (!status) return 'med';
  const s = status.toString().toUpperCase();
  if (s === 'PASSED') return 'ok';
  if (s === 'FAILED') return 'crit';
  return 'med';
}

function statusCodeClass(code) {
  if (!code) return 'med';
  if (code >= 500) return 'crit';
  if (code >= 400) return 'med';
  return 'ok';
}

// ── main component ─────────────────────────────────────────────────────────────
function DashboardPageV2() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const username = getDisplayName() || 'Developer';

  const [transactions, setTransactions] = useState([]);
  const [txStatus, setTxStatus]         = useState(TX_STATUS_IDLE);
  const [prEvents, setPrEvents]         = useState([]);
  const [alerts, setAlerts]             = useState([]);
  const [incidents, setIncidents]       = useState([]);
  const [loading, setLoading]           = useState(true);
  const [txLoading, setTxLoading]       = useState(false);
  const [error, setError]               = useState('');
  const [clock, setClock]               = useState('');
  const [dateStr, setDateStr]           = useState('');

  const [componentsList, setComponentsList]   = useState([]);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [githubDashboard, setGithubDashboard] = useState([]);
  const [tenantsList, setTenantsList]         = useState([]);
  const [selectedTenant, setSelectedTenant]   = useState(null);

  // filters
  const [timeRange, setTimeRange]       = useState('24h');
  const [dateFrom, setDateFrom]         = useState('');
  const [dateTo, setDateTo]             = useState('');
  const [envFilter, setEnvFilter]       = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');

  // clock
  useEffect(() => {
    document.body.style.overflow = (loading || txLoading) ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [loading, txLoading]);

  useEffect(() => {
    function tick() {
      const now = new Date();
      setClock(now.toLocaleTimeString('en-ZA'));
      setDateStr(now.toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }));
    }
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);

  // Mirrors txStatus so fetchTransactions can read the previous status without
  // taking it as a dependency — it needs the earlier loadedAt to tell a stale
  // view (we have older data) from a blank failure (we have nothing).
  const txStatusRef = useRef(TX_STATUS_IDLE);

  // fetch transactions — fans out per tenant × per component, merging all results
  const fetchTransactions = useCallback(async (range, from, to, compName, components, tenantId, tenants) => {
    setTxLoading(true);
    try {
      const makePromise = (comp, tId) => {
        if (from && to) {
          const today = new Date().toISOString().slice(0, 10);
          return getBusinessTransactionsByDateRange(
            new Date(`${today}T${from}:00`).toISOString(),
            new Date(`${today}T${to}:59`).toISOString(),
            comp || undefined,
            tId || undefined
          );
        }
        return getBusinessTransactionsByTimeFilter(range, comp || undefined, tId || undefined);
      };

      const compTargets = compName ? [compName] : (components?.length ? components : [null]);

      // resolve which tenant IDs to fan out across
      const resolveTId = (t) => typeof t === 'string' ? t : t.tenantId || t.id || t.name;
      const tenantTargets = tenantId
        ? [tenantId]
        : (tenants?.length ? tenants.map(resolveTId) : [null]);

      // one promise per (tenant, component) pair
      const promises = tenantTargets.flatMap(tId =>
        compTargets.map(comp => makePromise(comp, tId))
      );

      const results = await Promise.allSettled(promises);
      const rejected = results.filter(r => r.status === 'rejected');

      // Full detail stays here, never on screen (CWE-209).
      if (rejected.length > 0) {
        console.error(
          `[dashboard] ${rejected.length}/${results.length} transaction requests failed`,
          rejected.map(r => r.reason)
        );
      }

      const nowLabel = new Date().toLocaleTimeString('en-ZA');
      const next = nextTxStatus(results, txStatusRef.current, nowLabel);
      txStatusRef.current = next;
      setTxStatus(next);

      // When every request failed, keep whatever is already on screen rather
      // than blanking it to zeros — the TSB-4301 defect. A stale figure that
      // says it is stale beats a fresh-looking zero that is wrong.
      if (next.state !== 'stale' && next.state !== 'failed') {
        setTransactions(
          results
            .filter(r => r.status === 'fulfilled')
            .flatMap(r => {
              const d = r.value.data;
              return Array.isArray(d) ? d : d?.transactions || d?.content || [];
            })
        );
      }
    } catch (err) {
      // allSettled does not reject, so this only catches a fault in the code
      // above. Leave existing transactions in place.
      console.error('[dashboard] transaction fetch failed', err);
    } finally {
      setTxLoading(false);
    }
  }, []);

  // fetch everything else in parallel, then fan out per-component tx fetches
  const fetchAll = useCallback(async (range, from, to, compName, tenantId) => {
    setLoading(true);
    setTxLoading(true);
    setError('');

    const [prRes, alertRes, incRes, compRes, ghRes, tenantRes] = await Promise.allSettled([
      getPrEvents(),
      getAlerts(),
      getIncidents(),
      getComponents(),
      getGithubDashboard(),
      getTenants(),
    ]);

    if (prRes.status === 'fulfilled') {
      const d = prRes.value.data;
      setPrEvents(Array.isArray(d) ? d : d?.prEvents || d?.content || []);
    }
    if (alertRes.status === 'fulfilled') {
      const d = alertRes.value.data;
      setAlerts(Array.isArray(d) ? d : d?.alerts || d?.content || []);
    }
    if (incRes.status === 'fulfilled') {
      const d = incRes.value.data;
      setIncidents(Array.isArray(d) ? d : d?.incidents || d?.content || []);
    }

    const knownComponents = compRes.status === 'fulfilled'
      ? (Array.isArray(compRes.value.data) ? compRes.value.data : [])
      : [];
    setComponentsList(knownComponents);

    if (ghRes.status === 'fulfilled') {
      const d = ghRes.value.data;
      setGithubDashboard(Array.isArray(d) ? d : []);
    }

    let knownTenants = [];
    if (tenantRes.status === 'fulfilled') {
      const d = tenantRes.value.data;
      knownTenants = Array.isArray(d) ? d : d?.tenants || d?.content || [];
      setTenantsList(knownTenants);
    }

    if ([prRes, alertRes, incRes].every(r => r.status === 'rejected')) {
      setError('Failed to load dashboard data.');
    }

    setLoading(false);
    // fan out tx calls per tenant × component
    await fetchTransactions(range, from, to, compName, knownComponents, tenantId, knownTenants);
  }, [fetchTransactions]);

  // initial load
  useEffect(() => { fetchAll(timeRange, dateFrom, dateTo, selectedComponent, selectedTenant); }, [fetchAll]); // eslint-disable-line react-hooks/exhaustive-deps

  // re-fetch transactions when filters change (skip initial mount)
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    fetchTransactions(timeRange, dateFrom, dateTo, selectedComponent, componentsList, selectedTenant, tenantsList);
  }, [timeRange, dateFrom, dateTo, selectedComponent, selectedTenant, fetchTransactions]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── derived data ─────────────────────────────────────────────────────────────
  const filteredTx = useMemo(() => {
    let tx = transactions;
    if (envFilter !== 'all') tx = tx.filter(t => t.environment === envFilter);
    if (serviceFilter !== 'all') tx = tx.filter(t => t.componentName === serviceFilter);
    return tx;
  }, [transactions, envFilter, serviceFilter]);

  const txStats = useMemo(() => {
    const total  = filteredTx.length;
    const s2xx   = filteredTx.filter(t => t.statusCode >= 200 && t.statusCode < 300).length;
    const s4xx   = filteredTx.filter(t => t.statusCode >= 400 && t.statusCode < 500).length;
    const s5xx   = filteredTx.filter(t => t.statusCode >= 500).length;
    const durations = filteredTx.map(t => t.durationMs).filter(Boolean);
    const avgDuration = durations.length ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length) : 0;
    const pct2xx = total ? +((s2xx / total) * 100).toFixed(1) : 0;
    const pct4xx = total ? +((s4xx / total) * 100).toFixed(1) : 0;
    const pct5xx = total ? +((s5xx / total) * 100).toFixed(1) : 0;
    return { total, s2xx, s4xx, s5xx, avgDuration, pct2xx, pct4xx, pct5xx };
  }, [filteredTx]);

  const prStats = useMemo(() => {
    const prs = selectedComponent ? prEvents.filter(p => (p.repository || '') === selectedComponent) : prEvents;
    return {
      total:   prs.length,
      passed:  prs.filter(p => p.status === 'PASSED').length,
      failed:  prs.filter(p => p.status === 'FAILED').length,
      pending: prs.filter(p => p.status === 'PENDING').length,
    };
  }, [prEvents, selectedComponent]);

  const incidentStats = useMemo(() => {
    const inc = selectedComponent ? incidents.filter(i => (i.service || '') === selectedComponent) : incidents;
    return {
      total:    inc.length,
      critical: inc.filter(i => (i.severity || i.status || '').toUpperCase() === 'CRITICAL').length,
      high:     inc.filter(i => (i.severity || '').toUpperCase() === 'HIGH').length,
      open:     inc.filter(i => !['RESOLVED', 'CLOSED'].includes((i.status || '').toUpperCase())).length,
    };
  }, [incidents, selectedComponent]);

  const alertStats = useMemo(() => {
    const al = selectedComponent ? alerts.filter(a => (a.service || '') === selectedComponent) : alerts;
    return {
      total:    al.length,
      critical: al.filter(a => (a.severity || '').toUpperCase() === 'CRITICAL').length,
      high:     al.filter(a => (a.severity || '').toUpperCase() === 'HIGH').length,
      medium:   al.filter(a => (a.severity || '').toUpperCase() === 'MEDIUM').length,
      alarm:    al.filter(a => (a.state || a.status || '').toUpperCase() === 'ALARM').length,
    };
  }, [alerts, selectedComponent]);

  const components = useMemo(() => [...new Set(transactions.map(t => t.componentName).filter(Boolean))], [transactions]);
  const environments = useMemo(() => [...new Set(transactions.map(t => t.environment).filter(Boolean))], [transactions]);

  const componentHealth = useMemo(() => components.map(comp => {
    const txs     = filteredTx.filter(t => t.componentName === comp);
    const success = txs.filter(t => t.statusCode >= 200 && t.statusCode < 300).length;
    const pct     = txs.length ? +((success / txs.length) * 100).toFixed(1) : 100;
    return { comp, pct, total: txs.length };
  }), [filteredTx, components]);

  const reqByComponent = useMemo(() => components.map(comp => ({
    comp,
    count: filteredTx.filter(t => t.componentName === comp).length,
  })).sort((a, b) => b.count - a.count), [filteredTx, components]);

  const recentActivity = useMemo(() =>
    [...filteredTx].sort((a, b) => new Date(b.timestamp || b.createdAt) - new Date(a.timestamp || a.createdAt)).slice(0, 2),
    [filteredTx]
  );

  const openIncidents = useMemo(() => {
    let inc = incidents.filter(i => !['RESOLVED', 'CLOSED'].includes((i.status || '').toUpperCase()));
    if (selectedComponent) inc = inc.filter(i => (i.service || '') === selectedComponent);
    return inc.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 5);
  }, [incidents, selectedComponent]);

  const activeAlerts = useMemo(() => {
    let al = alerts.filter(a => (a.state || a.status || '').toUpperCase() !== 'OK');
    if (selectedComponent) al = al.filter(a => (a.service || '') === selectedComponent);
    return al.sort((a, b) => new Date(b.createdAt || b.timestamp) - new Date(a.createdAt || a.timestamp)).slice(0, 6);
  }, [alerts, selectedComponent]);

  const recentPrs = useMemo(() => {
    let prs = [...prEvents];
    if (selectedComponent) prs = prs.filter(p => (p.repository || '') === selectedComponent);
    return prs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 4);
  }, [prEvents, selectedComponent]);

  const hourlyVolume = useMemo(() => {
    const buckets = Array(24).fill(0);
    filteredTx.forEach(t => {
      const d = new Date(t.timestamp || t.createdAt);
      if (!isNaN(d)) buckets[d.getHours()]++;
    });
    return buckets;
  }, [filteredTx]);

  const hourlyAvgDuration = useMemo(() => {
    const sums   = Array(24).fill(0);
    const counts = Array(24).fill(0);
    filteredTx.forEach(t => {
      if (t.durationMs == null) return;
      const d = new Date(t.timestamp || t.createdAt);
      if (!isNaN(d)) { sums[d.getHours()] += t.durationMs; counts[d.getHours()]++; }
    });
    return sums.map((s, i) => counts[i] ? Math.round(s / counts[i]) : 0);
  }, [filteredTx]);

  const hourLabels = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`);

  const retryTransactions = useCallback(() => {
    fetchTransactions(timeRange, dateFrom, dateTo, selectedComponent, componentsList, selectedTenant, tenantsList);
  }, [fetchTransactions, timeRange, dateFrom, dateTo, selectedComponent, componentsList, selectedTenant, tenantsList]);

  // A failed batch must not read as "nothing happened" — that conflation is the
  // TSB-4301 defect. Empty is a fact; failed is an unknown.
  const txEmptyText = isBlankFailure(txStatus)
    ? "Couldn't load transaction data."
    : 'No transactions in this period.';
  const compEmptyText = isBlankFailure(txStatus)
    ? "Couldn't load component data."
    : 'No component data in this period.';

  // morning/afternoon/evening greeting
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const firstName = username.split(' ')[0];

  return (
    <div className={`v2-shell${theme === 'light' ? ' v2-light' : ''}`}>
      <div className="v2-main">
        {(loading || txLoading) && <LoadingSpinner message={loading ? "Loading dashboard…" : "Fetching transactions…"} />}

        {/* HERO */}
        <div className="v2-hero">
          <div className="v2-hero-top">
            <div>
              <div className="v2-hero-greeting">Operations Dashboard</div>
              <div className="v2-hero-title">{greeting}, <span>{firstName}</span></div>
              <div className="v2-hero-sub">Here's what's happening across your systems right now.</div>
            </div>
            <div className="v2-hero-right">
              {alertStats.alarm > 0 && (
                <div className="v2-status-chip">
                  <div className="v2-chip-dot" />
                  {alertStats.alarm} Alert{alertStats.alarm !== 1 ? 's' : ''} in Alarm
                </div>
              )}
              <div className="v2-hero-time">
                <div className="v2-hero-clock">{clock}</div>
                <div className="v2-hero-date">{dateStr}</div>
              </div>
            </div>
          </div>

          {/* KPI strip */}
          <div className="v2-hero-kpis">
            <div className="v2-hkpi v2-hkpi--cq">
              <div className="v2-hkpi-label">Total Transactions</div>
              <div className="v2-hkpi-value">{txStats.total.toLocaleString()}</div>
              <div className="v2-hkpi-sub">
                {dateFrom && dateTo
                  ? `${dateFrom} → ${dateTo}`
                  : `last ${timeRange === '1h' ? '1 hour' : timeRange === '2h' ? '2 hours' : '24 hours'}`}
              </div>
              <div className="v2-hkpi-glow" />
            </div>
            <div className="v2-hkpi v2-hkpi--green">
              <div className="v2-hkpi-label">TX Success Rate</div>
              <div className="v2-hkpi-value">{txStats.pct2xx}%</div>
              <div className="v2-hkpi-sub v2-up">{txStats.s2xx.toLocaleString()} successful</div>
              <div className="v2-hkpi-glow" />
            </div>
            <div className="v2-hkpi v2-hkpi--orange">
              <div className="v2-hkpi-label">Avg Response Time</div>
              <div className="v2-hkpi-value">{txStats.avgDuration > 0 ? `${txStats.avgDuration}ms` : '—'}</div>
              <div className="v2-hkpi-sub">across all services</div>
              <div className="v2-hkpi-glow" />
            </div>
            <div className="v2-hkpi v2-hkpi--red">
              <div className="v2-hkpi-label">Open Incidents</div>
              <div className="v2-hkpi-value">{incidentStats.open}</div>
              <div className="v2-hkpi-sub v2-down">{incidentStats.critical} critical</div>
              <div className="v2-hkpi-glow" />
            </div>
            <div className="v2-hkpi v2-hkpi--yellow">
              <div className="v2-hkpi-label">Active Alerts</div>
              <div className="v2-hkpi-value">{alertStats.alarm}</div>
              <div className="v2-hkpi-sub">{alertStats.critical} critical in alarm</div>
              <div className="v2-hkpi-glow" />
            </div>
            <div className="v2-hkpi v2-hkpi--purple">
              <div className="v2-hkpi-label">PIR Pending</div>
              <div className="v2-hkpi-value">{prStats.failed}</div>
              <div className="v2-hkpi-sub">failed PRs need review</div>
              <div className="v2-hkpi-glow" />
            </div>
          </div>
        </div>

        {/* FILTER BAR */}
        <div className="v2-filter-bar">
          <span className="v2-filter-label">Transactions</span>

          {/* preset time buttons */}
          <div className="v2-time-pills">
            {[
              { val: '1h',  label: '1h'  },
              { val: '2h',  label: '2h'  },
              { val: '24h', label: '24h' },
            ].map(({ val, label }) => (
              <button
                key={val}
                className={`v2-time-pill${timeRange === val && !dateFrom && !dateTo ? ' v2-time-pill--active' : ''}`}
                onClick={() => { setTimeRange(val); setDateFrom(''); setDateTo(''); }}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="v2-filter-divider" />

          {/* tenant dropdown — searchable, tenant ids are opaque hex (TSB-4302) */}
          <SearchableSelect
            value={selectedTenant || ''}
            options={tenantsList.map(t => {
              const id = typeof t === 'string' ? t : t.tenantId || t.id || t.name;
              return { id, label: id };
            })}
            onChange={id => setSelectedTenant(id || null)}
            allLabel="All Tenants"
            placeholder="Search tenants…"
          />

          <div className="v2-filter-divider" />

          {/* component dropdown */}
          <SearchableSelect
            value={selectedComponent || ''}
            options={componentsList.map(c => ({ id: c, label: c }))}
            onChange={id => setSelectedComponent(id || null)}
            allLabel="All Components"
            placeholder="Search components…"
          />

          <div className="v2-filter-divider" />

          {/* Custom time range (today only).
              Labelled because two empty --:-- boxes say nothing about what they
              are for (TSB-4439). The visible label names the pair; the
              aria-labels name each end, since "Time" alone would leave a screen
              reader unable to tell the two inputs apart. */}
          <span className="v2-filter-label">Time</span>
          <div className="v2-date-range">
            <input
              type="time"
              aria-label="From time"
              className={`v2-date-input${dateFrom ? ' v2-date-input--active' : ''}`}
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
            />
            <span className="v2-date-sep" aria-hidden="true">→</span>
            <input
              type="time"
              aria-label="To time"
              className={`v2-date-input${dateTo ? ' v2-date-input--active' : ''}`}
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
            />
            {(dateFrom || dateTo) && (
              <button
                type="button"
                aria-label="Clear time range"
                className="v2-date-clear"
                onClick={() => { setDateFrom(''); setDateTo(''); }}
              >
                ✕
              </button>
            )}
          </div>

          <div className="v2-filter-divider" />

          <select className="v2-filter-select" value={envFilter} onChange={e => setEnvFilter(e.target.value)}>
            <option value="all">All Environments</option>
            {environments.map(e => <option key={e} value={e}>{e}</option>)}
          </select>

          <div className="v2-filter-divider" />

          <select className="v2-filter-select" value={serviceFilter} onChange={e => setServiceFilter(e.target.value)}>
            <option value="all">All Services</option>
            {components.map(c => <option key={c} value={c}>{c}</option>)}
          </select>

          <div className="v2-filter-spacer" />

<button className="v2-filter-btn v2-filter-btn--present" onClick={() => navigate('/presentation')}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>
            Present
          </button>
          <button className="v2-filter-btn" onClick={() => fetchAll(timeRange, dateFrom, dateTo, selectedComponent, selectedTenant)}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-.02-6.63"/></svg>
            Refresh
          </button>
        </div>

        {/* BODY */}
        <div className="v2-body">
          {error && <div className="v2-error">{error}</div>}

          <TransactionStatusBanner status={txStatus} onRetry={retryTransactions} />

          {/* ── TRANSACTION HEALTH ── */}
          <div className="v2-section-divider">
            <span className="v2-section-label">Transaction Health</span>
            {/* figures computed from an incomplete set must not read as authoritative */}
            {isDegraded(txStatus) && <span className="v2-degraded-chip">Partial</span>}
            <div className="v2-section-line" />
          </div>

          <div className="v2-tx-row">
            {/* Volume chart */}
            <div className="v2-card v2-card--chart">
              <div className="v2-card-header">
                <div className="v2-card-title">Transaction Volume </div>
                <div className="v2-card-meta">bucketed by hour</div>
              </div>
              <VolumeChart data={hourlyVolume} labels={hourLabels} />
            </div>

            {/* Status code rings */}
            <div className="v2-arc-card">
              <div className="v2-arc-wrap">
                <TickRing pct={txStats.pct2xx} color="#00d97e" dimColor="rgba(0,217,126,0.12)" animate />
                <div className="v2-arc-label">
                  <span className="v2-arc-val" style={{ color: '#00d97e' }}>{txStats.pct2xx}%</span>
                </div>
              </div>
              <div className="v2-arc-body">
                <div className="v2-arc-name">2xx Success</div>
                <div className="v2-arc-count">{txStats.s2xx.toLocaleString()} requests</div>
              </div>
            </div>

            <div className="v2-arc-card">
              <div className="v2-arc-wrap">
                <TickRing pct={txStats.pct4xx} color="#ffb830" dimColor="rgba(255,184,48,0.12)" animate />
                <div className="v2-arc-label">
                  <span className="v2-arc-val" style={{ color: '#ffb830' }}>{txStats.pct4xx}%</span>
                </div>
              </div>
              <div className="v2-arc-body">
                <div className="v2-arc-name">4xx Client Errors</div>
                <div className="v2-arc-count">{txStats.s4xx.toLocaleString()} requests</div>
              </div>
            </div>

            <div className="v2-arc-card">
              <div className="v2-arc-wrap">
                <TickRing pct={txStats.pct5xx} color="#ff4d6d" dimColor="rgba(255,77,109,0.12)" animate />
                <div className="v2-arc-label">
                  <span className="v2-arc-val" style={{ color: '#ff4d6d' }}>{txStats.pct5xx}%</span>
                </div>
              </div>
              <div className="v2-arc-body">
                <div className="v2-arc-name">5xx Server Errors</div>
                <div className="v2-arc-count">{txStats.s5xx.toLocaleString()} requests</div>
              </div>
            </div>
          </div>

          {/* ── INFRASTRUCTURE ── */}
          <div className="v2-section-divider">
            <span className="v2-section-label">
              {selectedComponent ? `Component Spotlight — ${selectedComponent}` : 'Infrastructure'}
            </span>
            <div className="v2-section-line" />
          </div>

          {selectedComponent ? (
            /* ── SINGLE COMPONENT SPOTLIGHT ── */
            <div className="v2-spotlight-row">
              {/* Health ring */}
              <div className="v2-card v2-spotlight-ring-card">
                <div className="v2-card-title">Health Score</div>
                <div className="v2-spotlight-ring-wrap">
                  {(() => {
                    const h = componentHealth.find(c => c.comp === selectedComponent);
                    const pct = h ? h.pct : 100;
                    const color = pct >= 95 ? '#00d97e' : pct >= 80 ? '#ffb830' : '#ff4d6d';
                    const dim   = pct >= 95 ? 'rgba(0,217,126,0.12)' : pct >= 80 ? 'rgba(255,184,48,0.12)' : 'rgba(255,77,109,0.12)';
                    const label = pct >= 95 ? 'Healthy' : pct >= 80 ? 'Degraded' : 'Unhealthy';
                    return (
                      <GaugeWidget pct={pct} light={theme === 'light'} />
                    );
                  })()}
                </div>
              </div>

              {/* Avg Response Time chart */}
              <div className="v2-card v2-card--chart v2-spotlight-stats-card">
                <div className="v2-card-header">
                  <div className="v2-card-title">Avg Response Time</div>
                  <div className="v2-card-meta">
                    {txStats.avgDuration > 0 ? <span style={{ color: '#ff7c43', fontWeight: 700 }}>{txStats.avgDuration}ms overall</span> : 'by hour'}
                  </div>
                </div>
                <ResponseTimeChart data={hourlyAvgDuration} labels={hourLabels} />
              </div>

              {/* Recent activity */}
              <div className="v2-card v2-card--flex">
                <div className="v2-card-header">
                  <div className="v2-card-title">Recent Activity</div>
                  <div className="v2-card-meta">{filteredTx.length} total</div>
                </div>
                <div className="v2-item-list">
                  {recentActivity.length === 0 ? (
                    <p className="v2-empty">{txEmptyText}</p>
                  ) : recentActivity.map((tx, i) => (
                    <div
                      key={tx.id || i}
                      className="v2-item"
                      onClick={() => navigate(`/business-transactions/${tx.id}`)}
                    >
                      <div className={`v2-item-dot v2-dot--${statusCodeClass(tx.statusCode)}`} />
                      <div className="v2-item-body">
                        <div className="v2-item-title">{tx.endpoint || tx.httpMethod || '—'}</div>
                        <div className="v2-item-sub">{tx.durationMs != null ? `${tx.durationMs}ms` : '—'}</div>
                      </div>
                      <span className={`v2-b v2-b--${statusCodeClass(tx.statusCode)}`}>{tx.statusCode}</span>
                    </div>
                  ))}
                </div>
                {filteredTx.length > 2 && (
                  <div className="v2-view-all" onClick={() => navigate(`/business-transactions?component=${selectedComponent}`)}>
                    +{filteredTx.length - 2} more — View all →
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* ── ALL COMPONENTS VIEW ── */
            <div className="v2-infra-row">
              {/* Component health */}
              <div className="v2-card">
                <div className="v2-card-title">Component Health</div>
                <div className="v2-h-list">
                  {componentHealth.length === 0 ? (
                    <p className="v2-empty">{compEmptyText}</p>
                  ) : componentHealth.map(({ comp, pct }) => {
                    const cls = pct >= 95 ? 'healthy' : pct >= 80 ? 'degraded' : 'unhealthy';
                    const color = pct >= 95 ? '#00d97e' : pct >= 80 ? '#ffb830' : '#ff4d6d';
                    const dim   = pct >= 95 ? 'rgba(0,217,126,0.12)' : pct >= 80 ? 'rgba(255,184,48,0.12)' : 'rgba(255,77,109,0.12)';
                    return (
                      <div key={comp}>
                        <div className="v2-h-row-top">
                          {/* title so the full name survives truncation */}
                          <span className="v2-h-name" title={comp}>{comp}</span>
                          <span className={`v2-h-pct v2-h-pct--${cls}`}>{pct}%</span>
                        </div>
                        <SegBar pct={pct} color={color} dimColor={dim} />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Requests per component */}
              <div className="v2-card v2-card--flex">
                <div className="v2-card-header">
                  <div className="v2-card-title">Requests per Component</div>
                  <div className="v2-card-meta">counted from tx · filtered</div>
                </div>
                <div className="v2-req-chart-wrap">
                  <ReqBarChart data={reqByComponent} />
                </div>
              </div>

              {/* Recent activity */}
              <div className="v2-card v2-card--flex">
                <div className="v2-card-header">
                  <div className="v2-card-title">Recent Activity</div>
                  <div className="v2-card-meta">{filteredTx.length} total</div>
                </div>
                <div className="v2-item-list">
                  {recentActivity.length === 0 ? (
                    <p className="v2-empty">{txEmptyText}</p>
                  ) : recentActivity.map((tx, i) => (
                    <div
                      key={tx.id || i}
                      className="v2-item"
                      onClick={() => navigate(`/business-transactions/${tx.id}`)}
                    >
                      <div className={`v2-item-dot v2-dot--${statusCodeClass(tx.statusCode)}`} />
                      <div className="v2-item-body">
                        <div className="v2-item-title">{tx.componentName || '—'}</div>
                        <div className="v2-item-sub">{tx.endpoint || tx.httpMethod || '—'} · {tx.durationMs != null ? `${tx.durationMs}ms` : ''}</div>
                      </div>
                      <span className={`v2-b v2-b--${statusCodeClass(tx.statusCode)}`}>{tx.statusCode}</span>
                    </div>
                  ))}
                </div>
                {filteredTx.length > 2 && (
                  <div className="v2-view-all" onClick={() => navigate('/business-transactions')}>
                    +{filteredTx.length - 2} more transactions — View all →
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── INCIDENTS & ALERTS ── */}
          <div className="v2-section-divider">
            <span className="v2-section-label">Incidents & Alerts</span>
            <div className="v2-section-line" />
          </div>

          <div className="v2-ia-row">
            {/* Incidents */}
            <div className="v2-card v2-card--flex">
              <div className="v2-card-header">
                <div className="v2-card-title">Open Incidents</div>
                <div className="v2-card-meta" style={{ cursor: 'pointer' }} onClick={() => navigate('/incidents')}>View all →</div>
              </div>
              <div className="v2-sev-pills">
                {[
                  { label: 'Critical', val: incidentStats.critical, cls: 'crit' },
                  { label: 'High',     val: incidentStats.high,     cls: 'high' },
                  { label: 'Open',     val: incidentStats.open,     cls: 'med'  },
                  { label: 'Total',    val: incidentStats.total,    cls: 'muted'},
                ].map(p => (
                  <div key={p.label} className={`v2-sev-pill v2-sev-pill--${p.cls}`}>
                    <div className="v2-sev-val">{p.val}</div>
                    <div className="v2-sev-lbl">{p.label}</div>
                  </div>
                ))}
              </div>
              <div className="v2-item-list">
                {openIncidents.length === 0 ? (
                  <p className="v2-empty">No open incidents.</p>
                ) : openIncidents.map((inc, i) => (
                  <div
                    key={inc.incidentId || i}
                    className="v2-item"
                    onClick={() => navigate(`/incidents/${inc.service}/${inc.incidentId}`)}
                  >
                    <div className={`v2-item-dot v2-dot--${severityClass(inc.severity)}`} />
                    <div className="v2-item-body">
                      <div className="v2-item-title">{inc.title || inc.incidentId || `INC-${String(i).padStart(4,'0')}`}</div>
                      <div className="v2-item-sub">{inc.service || '—'} · {timeAgo(inc.createdAt)}</div>
                    </div>
                    <div className="v2-item-end">
                      <span className={`v2-b v2-b--${severityClass(inc.severity)}`}>{inc.severity || 'Unknown'}</span>
                      <span className={`v2-b v2-b--med`}>{inc.status || '—'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Alerts */}
            <div className="v2-card v2-card--flex">
              <div className="v2-card-header">
                <div className="v2-card-title">Active Alerts</div>
                <div className="v2-card-meta" style={{ cursor: 'pointer' }} onClick={() => navigate('/alerts')}>View all →</div>
              </div>
              <div className="v2-sev-pills">
                {[
                  { label: 'Critical', val: alertStats.critical, cls: 'crit'  },
                  { label: 'High',     val: alertStats.high,     cls: 'high'  },
                  { label: 'Medium',   val: alertStats.medium,   cls: 'med'   },
                  { label: 'Alarm',    val: alertStats.alarm,    cls: 'muted' },
                ].map(p => (
                  <div key={p.label} className={`v2-sev-pill v2-sev-pill--${p.cls}`}>
                    <div className="v2-sev-val">{p.val}</div>
                    <div className="v2-sev-lbl">{p.label}</div>
                  </div>
                ))}
              </div>
              <div className="v2-item-list">
                {activeAlerts.length === 0 ? (
                  <p className="v2-empty">No active alerts.</p>
                ) : activeAlerts.map((al, i) => (
                  <div
                    key={al.alertId || i}
                    className="v2-item"
                    onClick={() => navigate(`/alerts/${al.alertId}`)}
                  >
                    <div className={`v2-item-dot v2-dot--${severityClass(al.severity)}`} />
                    <div className="v2-item-body">
                      <div className="v2-item-title">{al.alertName || al.title || al.alertId || '—'}</div>
                      <div className="v2-item-sub">{al.componentName || al.service || '—'} · {timeAgo(al.createdAt || al.timestamp)}</div>
                    </div>
                    <div className="v2-item-end">
                      <span className={`v2-b v2-b--${severityClass(al.severity)}`}>{al.severity || 'Unknown'}</span>
                      <span className={`v2-b v2-b--${al.state === 'ALARM' ? 'crit' : 'med'}`}>{al.state || al.status || '—'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── PIPELINE ── */}
          <div className="v2-section-divider">
            <span className="v2-section-label">Pipeline</span>
            <div className="v2-section-line" />
          </div>

          <div className="v2-pr-row">
            {/* PR trend chart (pr-events system) */}
            <div className="v2-card v2-card--flex">
              <div className="v2-card-header">
                <div className="v2-card-title">PR Pass / Fail — {timeRange === '1h' ? 'Last 1 Hour' : timeRange === '2h' ? 'Last 2 Hours' : 'Last 24 Hours'}</div>
                <div style={{ display: 'flex', gap: 14 }}>
                  <div className="v2-legend-item"><div className="v2-legend-dot" style={{ background: '#00d97e' }} />Passed</div>
                  <div className="v2-legend-item"><div className="v2-legend-dot" style={{ background: '#ff4d6d' }} />Failed</div>
                  <div className="v2-legend-item"><div className="v2-legend-dot" style={{ background: '#ffb830' }} />Pending</div>
                </div>
              </div>
              <PrTrendChart prEvents={prEvents} timeRange={timeRange} />
            </div>

            {/* GitHub PRs widget */}
            <div className="v2-card v2-card--flex">
              <div className="v2-card-header">
                <div className="v2-card-title">GitHub Pull Requests</div>
                <div className="v2-card-meta" style={{ cursor: 'pointer' }} onClick={() => navigate('/github-prs')}>View all →</div>
              </div>
              {githubDashboard.length === 0 ? (
                <p className="v2-empty">No GitHub data. Configure <code style={{ fontSize: 11, background: 'rgba(255,255,255,0.06)', padding: '1px 5px', borderRadius: 4 }}>GITHUB_MONITORED_REPOS</code> in the backend.</p>
              ) : (
                <>
                  {/* per-repo summary pills */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
                    {githubDashboard.map(rd => {
                      const pending  = rd.pendingPullRequests?.length  || 0;
                      const approved = rd.approvedPullRequests?.length || 0;
                      const deploys  = rd.recentDeployments?.length    || 0;
                      return (
                        <div key={rd.repo} className="v2-gh-repo-row">
                          <span className="v2-gh-repo-name">{rd.repo}</span>
                          <div className="v2-gh-pills">
                            <span className="v2-gh-pill v2-gh-pill--pending">{pending} pending</span>
                            <span className="v2-gh-pill v2-gh-pill--approved">{approved} approved</span>
                            <span className="v2-gh-pill v2-gh-pill--deploy">{deploys} deploys</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* recent open PRs across all repos */}
                  <div className="v2-item-list">
                    {githubDashboard
                      .flatMap(rd => (rd.pendingPullRequests || []).map(pr => ({ ...pr, _repo: rd.repo })))
                      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
                      .slice(0, 5)
                      .map((pr, i) => (
                        <a
                          key={pr.number || i}
                          className="v2-item v2-item--link"
                          href={pr.htmlUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'rgba(191,20,204,0.2)', color: '#bf14cc', fontSize: 9, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {(pr.author || '?').slice(0, 2).toUpperCase()}
                          </div>
                          <div className="v2-item-body">
                            <div className="v2-item-title">{pr._repo} #{pr.number}</div>
                            <div className="v2-item-sub">{pr.title}</div>
                          </div>
                          <span className={`v2-b v2-b--${pr.reviewState === 'APPROVED' ? 'ok' : pr.reviewState === 'CHANGES_REQUESTED' ? 'crit' : 'med'}`}>
                            {pr.reviewState || 'PENDING'}
                          </span>
                        </a>
                      ))
                    }
                  </div>
                </>
              )}
            </div>
          </div>

          <div style={{ height: 24 }} />
        </div>
      </div>
    </div>
  );
}

export default DashboardPageV2;
